import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/client";
import { AuthContext } from "../contexts/AuthContext";

const STORAGE_KEY = "welcomeOfferBannerDismissed";

/**
 * Persistent welcome banner for new accounts.
 * Shown to logged-in users who still qualify for a superadmin "new_users" offer.
 * It stays available across reloads (per session) until the user places their
 * first order — once they do, /api/offers/welcome returns no offers and it hides.
 * The close button hides it for the current session only.
 */
const WelcomeOfferBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading } = useContext(AuthContext);
  const [offers, setOffers] = useState([]);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (loading || !user?.id) {
      setVisible(false);
      setOffers([]);
      return;
    }
    if (sessionStorage.getItem(STORAGE_KEY) === "true") return;

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/api/offers/welcome");
        if (cancelled) return;
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        if (res.data?.isNewUser && data.length > 0) {
          setOffers(data);
          setVisible(true);
        }
      } catch {
        /* silent — banner is non-critical */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, loading]);

  if (!visible || !user?.id) return null;

  const bestCents = offers.reduce(
    (max, o) => ((Number(o.discountCents) || 0) > max ? Number(o.discountCents) || 0 : max),
    0
  );
  const value = `€${(bestCents / 100).toFixed(2)}`;

  const handleClose = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "true");
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const handleCta = () => {
    navigate("/explore");
  };

  return (
    <div
      className="fixed top-3 left-3 right-3 z-[9996] max-w-md mx-auto"
      role="dialog"
      aria-label={t("welcomeOffer.title")}
    >
      <div className="rounded-xl border-2 border-brand/25 bg-white shadow-elevated overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand/15 flex items-center justify-center text-lg">
            🎁
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{t("welcomeOffer.title")}</p>
            <p className="text-gray-500 text-xs mt-0.5">
              {t("welcomeOffer.bannerText", { value })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCta}
              className="py-2 px-3 rounded-lg bg-brand hover:bg-brand-dark text-white font-medium text-sm transition-colors"
            >
              {t("welcomeOffer.bannerCta")}
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={t("welcomeOffer.continue")}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeOfferBanner;
